import { ApplicationSettings } from '../../models/settings';
import BaseComponentModel from './base-component-model';
import IImage from '../../models/viewModels/base/IImage';
import { IMapPoint } from '../../models/map/IMapPoint';
import { Entry } from 'contentful';
import IShouldReturnEntity from '../../api-clients/IShouldReturnEntity';
import { ExcludedEntitiesHelper } from '../../api-clients/page-template-clients/helpers/excluded-entities-helper';

export default class MapComponentModel extends BaseComponentModel {
  zoomLevel: number;
  fallbackImage: IImage;
  points: IMapPoint[];
  googleApiKey: string;
  id?: string;
  protected shouldReturnEntity(entry: Entry<any>): IShouldReturnEntity {
    const requiredFields = ['markerArray'];
    const notEmptyFields = ['markerArray'];

    return this.excludedEntitiesHelper.shouldComponentBeReturned(
      this.component,
      entry,
      requiredFields,
      notEmptyFields,
    );
  }

  protected shouldReturnChildEntities(entry: Entry<any>): IShouldReturnEntity {
    const requiredFields = [
      'fields',
      'fields.title',
      'fields.location',
      'fields.location.lat',
      'fields.location.lon',
    ];
    const notEmptyFields = [];

    return this.excludedEntitiesHelper.shouldComponentBeReturned(
      this.component,
      entry,
      requiredFields,
      notEmptyFields,
    );
  }

  public excludedEntitiesHelper = new ExcludedEntitiesHelper();
  public component = 'Map';

  constructor(data: any, appSettings: ApplicationSettings) {
    super('map', appSettings);

    const mapShouldReturn = this.shouldReturnEntity(data);

    if (!mapShouldReturn.shouldReturn) {
      this.exclusionReasons.push(...mapShouldReturn.shouldNotReturnReasons);
      this.isValid = false;
      return;
    }

    this.zoomLevel = data.zoomLevel;
    if (
      data.fallbackImage &&
      data.fallbackImage?.fields &&
      data.fallbackImage?.fields?.image?.length
    ) {
      this.fallbackImage = {
        secureUrl: data.fallbackImage.fields.image[0].secure_url,
        alt: data.fallbackImage.fields.altText,
        sizes: data.fallbackImage.fields.size,
        format: `f_jpg`,
        resourceType: `image`,
      };
    }

    this.id = data.id;

    this.points = data.markerArray.map((x) => {
      const mapShouldReturnChildEntities = this.shouldReturnChildEntities(x);

      if (!mapShouldReturnChildEntities.shouldReturn) {
        this.exclusionReasons.push(...mapShouldReturnChildEntities.shouldNotReturnReasons);
        this.isValid = false;

        return null;
      }

      return {
        title: x.fields.title,
        description: x.fields.description,
        location: {
          lat: x.fields.location.lat,
          lng: x.fields.location.lon,
        },
        icon: {
          secureUrl: this.getIconUrl(x.fields.icon?.fields?.file?.url),
        },
      };
    });

    if (appSettings) {
      this.googleApiKey = appSettings.googleApiKey;
    }

    if (!this.googleApiKey || !this.points?.length) {
      this.isValid = false;
    }
  }

  private getIconUrl(url: string): string {
    return url?.startsWith(`//`) ? `https:${url}` : url;
  }
}
